import { useState, useEffect, useRef, useCallback } from "react";
import Stack from '@mui/material/Stack';
import { Select, MenuItem, FormControl, InputLabel, Dialog, DialogContent, DialogActions } from "@mui/material";
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import MicIcon from '@mui/icons-material/Mic';
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';

const FollowUpItemModal = (props) => {
    const [readOnly, setReadOnly] = useState(false);
    const [fieldsDisabled, setFieldsDisabled] = useState(false);
    const [currentMicQuestion, setCurrentMicQuestion] = useState("");
    const voiceTranscriptRef = useRef("");

    const {
        transcript,
        listening,
        resetTranscript,
        browserSupportsSpeechRecognition
    } = useSpeechRecognition();

    useEffect(() => {
        let questionId = props.currQuestion?.indexes[props.currQuestion?.indexes.length - 1];
        let action = props.currQuestion?.action;

        if(questionId !== undefined && questionId !== null && questionId !== "" && action !== "none") {
            setReadOnly(true);
        } else {
            setReadOnly(false);
        }

        // Disable fields when no question is selected, unless editing an existing no-question item
        if(!questionId || questionId === '') {
            let hasExistingContent = (props.comment || '').trim() !== '' || (props.followUpItem || '').trim() !== '';
            setFieldsDisabled(!hasExistingContent);
        } else {
            setFieldsDisabled(false);
        }
    }, [props.currQuestion, props.comment, props.followUpItem]);

    useEffect(() => {
        setReadOnly(props.read);
    }, [props.read]);

    useEffect(() => {
        if (!listening) {
            voiceTranscriptRef.current = "";
            resetTranscript();
        } else {
            if(currentMicQuestion === "comment") {
                voiceTranscriptRef.current = props.comment ? props.comment + ' ' : '';
            } else if(currentMicQuestion === "follow_up") {
                voiceTranscriptRef.current = props.followUpItem ? props.followUpItem + ' ' : '';
            }
        }
    }, [listening, props.open]);

    useEffect(() => {
        if (!listening) {
            return;
        }
        
        if(transcript) {
            if(currentMicQuestion === "comment") {
                props.onCommentChange({ target: { value: voiceTranscriptRef.current + replacePunctuationWords(transcript) } });
            } else if(currentMicQuestion === "follow_up") {
                props.onFollowUpItemChange({ target: { value: voiceTranscriptRef.current + replacePunctuationWords(transcript) } });
            }
        }
    }, [transcript]);

    const createMenuItems = () => {
        let menuItems = [];
        let questions = props.open ? flattenObj(props.data[props.currQuestion?.indexes[0]]) : [];
        let followUpItems = questions.follow_up?.answer;
        
        questions = Object.keys(questions)
            .filter((questionId) => {
                let exists = false;
                followUpItems.forEach((item) => {
                    if(item.id === questionId && questionId !== props.currQuestion?.indexes[props.currQuestion?.indexes.length - 1]) {
                        exists = true;
                    }
                });
                return !exists;
            })
            .reduce((obj, questionId) => {
                return Object.assign(obj, {
                [questionId]: questions[questionId]
            });
            }, {});
        for(let question in questions) {
            if(question !== "follow_up" && questions[question]?.hasOwnProperty("question") && questions[question]?.question[1] === '.')
                menuItems.push(<MenuItem sx={{ maxWidth: "800px", whiteSpace: 'normal' }} value={Object.keys(questions).find(key => questions[key] === questions[question])} key={question}>{questions[question]?.question}</MenuItem>);
        }
        return menuItems;
    }

    const flattenObj = (ob) => {
        let result = {...ob}
        Object.values(ob).forEach((item) => {
            if ((typeof item) === 'object' && !Array.isArray(item)) {
                if(!item.hasOwnProperty("question")) {
                    result = { ...result, ...item };
                    delete result.item;
                } 
            } 
        });
        return result;
    };

    const findPathByName = (obj, targetName) => {
        const helper = (currentObj, currentPath) => {
          for (let key in currentObj) {
            if (currentObj.hasOwnProperty(key)) {
              const newPath = [...currentPath, key];
              if (key === targetName) {
                return newPath;
              }
              if (typeof currentObj[key] === 'object' && currentObj[key] !== null) {
                const result = helper(currentObj[key], newPath);
                if (result) return result;
              }
            }
          }
          return null;
        }
        return helper(obj, []);
    }

    const replacePunctuationWords = (text) => {
        const punctuationMap = {
            'comma': ',',
            'period': '.',
            'question mark': '?',
            'exclamation mark': '!',
            'colon': ':',
            'semicolon': ';',
            'dash': '-',
            'hyphen': '-',
            'new line': '\n',
            'new paragraph': '\n\n'
        };

        // Replace punctuation words
        let processedText = text.replace(/\b(comma|period|question mark|exclamation mark|colon|semicolon|dash|hyphen|new line|new paragraph)\b/gi, match => {
            return punctuationMap[match.toLowerCase()] || match;
        });

        // Remove spaces before punctuation
        processedText = processedText.replace(/\s+([,\.?!:;\-\n])/g, '$1');

        return processedText;
    };
      
    const handleCommentChange = useCallback((e) => {
        props.onCommentChange(e);
        if (listening && currentMicQuestion === "comment") {
            voiceTranscriptRef.current = e.target.value;
            resetTranscript();
        } else if (listening) {
            SpeechRecognition.abortListening();
            resetTranscript();
        }
    }, [listening, currentMicQuestion]);

    const handleFollowUpChange = useCallback((e) => {
        props.onFollowUpItemChange(e);
        if (listening && currentMicQuestion === "follow_up") {
            voiceTranscriptRef.current = e.target.value;
            resetTranscript();
        } else if (listening) {
            SpeechRecognition.abortListening();
            resetTranscript();
        }
    }, [listening, currentMicQuestion]);

    const onCancel = () => {
        try {
            if(listening) {
                SpeechRecognition.abortListening();
            }
        } catch(e) {
            console.error("Error aborting speech recognition:", e);
        }
        props.handleClose();
    }

    const onConfirm = () => {
        try {
            if(listening) {
                SpeechRecognition.abortListening();
            }
        } catch(e) {
            console.error("Error aborting speech recognition:", e);
        }
        props.onModalConfirm();
    }

        

    return (
        <Dialog disableEnforceFocus open={props.open} onClose={onCancel} fullWidth maxWidth="sm" scroll="body" PaperProps={{ sx: { maxWidth: 500 } }}>
            <DialogContent sx={{ overflow: 'visible' }}>
                <Stack spacing={2}>
                    <Typography variant="h6">Comment/Follow Up Item</Typography>
                    <FormControl fullWidth size="small">
                        <InputLabel>Question</InputLabel>
                        <Select
                            label="Question"
                            variant="standard"
                            value={props.currQuestion?.indexes[props.currQuestion?.indexes.length - 1] || ""}
                            onChange={(e) => { 
                                let indexes = findPathByName(props.data?.[props.currQuestion?.indexes[0]], e.target.value);
                                indexes = [props.currQuestion?.indexes[0]].concat(indexes);
                                console.log(indexes);
                                
                                props.onQuestionChange(indexes);
                            }}
                            inputProps={{ readOnly: readOnly }}
                        >
                            {createMenuItems()}
                        </Select>
                    </FormControl>
                    <TextField 
                        variant={"outlined"} 
                        multiline minRows={3} 
                        maxRows={100} 
                        error={props.error} 
                        validate={true} 
                        label="Comment" 
                        value={props.comment}
                        onChange={handleCommentChange} 
                        disabled={fieldsDisabled}
                        sx={{ width: "100%" }} 
                        InputProps={{ 
                            readOnly: props.read, 
                            maxLength: 10000,
                            endAdornment: <InputAdornment position="end">
                                    <IconButton
                                        aria-label="speak"
                                        onClick={() => {
                                            if (!listening) {
                                                setCurrentMicQuestion("comment");
                                                SpeechRecognition.startListening({ continuous: true });
                                            } else {
                                                voiceTranscriptRef.current = "";
                                                SpeechRecognition.stopListening();
                                            }
                                        }}
                                        sx={{ alignSelf: "end" }}
                                        size="small"
                                    >
                                        <MicIcon sx={{ color: listening && currentMicQuestion === "comment" ? "#3fa7d6" : "grey" }} fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                        }}
                    />
                    <TextField 
                        variant={"outlined"} 
                        multiline minRows={3} 
                        maxRows={100} 
                        error={props.error} 
                        validate={true} 
                        label="Follow Up Item" 
                        value={props.followUpItem}
                        onChange={handleFollowUpChange} 
                        disabled={fieldsDisabled}
                        sx={{ width: "100%" }} 
                        inputProps={{ readOnly: props.read, maxLength: 10000 }}
                        InputProps={{ 
                            endAdornment: <InputAdornment position="end">
                                    <IconButton
                                        aria-label="speak"
                                        onClick={() => {
                                            if (!listening) {
                                                setCurrentMicQuestion("follow_up");
                                                SpeechRecognition.startListening({ continuous: true });
                                            } else {
                                                voiceTranscriptRef.current = "";
                                                SpeechRecognition.stopListening();
                                            }
                                        }}
                                        sx={{ alignSelf: "end" }}
                                        size="small"
                                    >
                                        <MicIcon sx={{ color: listening && currentMicQuestion === "follow_up" ? "#3fa7d6" : "grey" }} fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                        }}
                    />
                </Stack>
            </DialogContent>
            <DialogActions sx={{ justifyContent: "space-around", borderTop: "none", pt: 0, pb: 2 }}>
                <Button color="secondary" variant="contained" onClick={onCancel}>Cancel</Button>
                <Button variant="contained" onClick={onConfirm}>Confirm</Button>
            </DialogActions>
        </Dialog>
    )
}

export default FollowUpItemModal;
